import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';
import {
    FaSearch, FaBell, FaChevronDown, FaHome, FaNetworkWired,
    FaFlask, FaBookOpen, FaUsers, FaCog, FaPlus, FaCheck, FaTimes,
    FaGift, FaChartBar, FaFileAlt, FaEdit, FaEye, FaLink, FaBriefcase,
    FaClipboardList, FaUserFriends
} from 'react-icons/fa';
import './ResearcherPortal.css';

// ── MOCK DATA ─────────────────────────────────────────────────────────────
const MOCK_RES_USER = {
    name: 'Dr. Julián Rivera',
    initials: 'JR',
    role: 'Investigador Clínico Senior',
    avatar: null,
    hIndex: 34,
    cites: '2.8k',
};

const ResearcherPortal = () => {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activeNav, setActiveNav] = useState('inicio');
    const [networkAccepted, setNetworkAccepted] = useState(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    const displayUser = user?.name ? user : MOCK_RES_USER;
    const firstName = displayUser.name.split(' ').slice(-1)[0];

    const MOCK_TRIALS = [
        {
            id: 1,
            status: t('portal.trialStatus.recruiting'),
            statusColor: '#22c55e',
            phase: t('portal.trialPhase', { phase: 'II' }),
            title: 'Inmunoterapia Avanzada para Melanoma',
            progress: 68,
            progressLabel: 'Progreso de Reclutamiento',
            milestone: 'Revisión de Seguridad - 15 Oct',
        },
        {
            id: 2,
            status: t('portal.trialStatus.followUp'),
            statusColor: '#3b82f6',
            phase: t('portal.trialPhase', { phase: 'III' }),
            title: 'Estudio Multicéntrico Diabetes Tipo 1',
            progress: 42,
            progressLabel: 'Análisis de Datos',
            milestone: 'Reporte Semestral de Resultados',
        },
    ];

    const MOCK_PUBLICATIONS = [
        {
            id: 1,
            type: 'PDF',
            title: 'Impact of Checkpoint Inhibitors in Advanced Melanoma: A Meta-Analysis',
            journal: 'The Lancet Oncology',
            date: 'Sep 2023',
            doi: '10.1016/S1470-2045',
            views: '1.2k',
        },
        {
            id: 2,
            type: 'DOC',
            title: 'Precision Medicine in Type 1 Diabetes: Current Trends',
            journal: 'Diabetes Care',
            date: 'Jul 2023',
            doi: '10.2337/dc22-1234',
            views: '850',
        },
    ];

    const LIFECYCLE_ACTIONS = [
        { icon: <FaPlus />, label: t('lifecycle.newIdea'), route: '/researcher-ideation' },
        { icon: <FaFileAlt />, label: t('lifecycle.protocol'), route: '/protocol-editor' },
        { icon: <FaClipboardList />, label: t('lifecycle.management'), route: '/clinical-projects' },
        { icon: <FaEdit />, label: t('lifecycle.aiWriting') },
    ];

    const NAV_ITEMS = [
        { key: 'inicio', label: t('portal.nav.start'), icon: <FaHome />, path: '/researcher-community' },
        { key: 'red', label: t('portal.nav.network'), icon: <FaUsers />, path: '/researcher-network' },
        { key: 'proyectos', label: t('portal.nav.projects'), icon: <FaFlask />, path: '/clinical-projects' },
        { key: 'publicaciones', label: t('portal.nav.publications'), icon: <FaSearch />, path: '/researcher-publications' },
        { key: 'grupos', label: t('portal.nav.groups'), icon: <FaUserFriends />, path: '/researcher-groups' },
        { key: 'capacitacion', label: t('portal.nav.training'), icon: <FaBriefcase />, path: '/researcher-training' },
        { key: 'config', label: t('portal.nav.config'), icon: <FaCog />, path: null },
    ];

    const handleLogout = () => {
        logout();
        navigate('/landing');
    };

    return (
        <div className="rp-page">
            {/* ── LEFT SIDEBAR ── */}
            <aside className="rp-sidebar">
                <div className="rp-sidebar-top">
                    <Link to="/" className="rp-logo">
                        <img src="/logoecseia.png" alt="Logo" className="rp-logo-img" />
                        <span className="rp-logo-eco">Eco</span><span className="rp-logo-rest">ScienceInnova</span>
                    </Link>

                    <nav className="rp-nav">
                        {NAV_ITEMS.map(item => (
                            <button
                                key={item.key}
                                className={`rp-nav-item ${activeNav === item.key ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveNav(item.key);
                                    if (item.path) navigate(item.path);
                                }}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                <button className="rp-new-btn" onClick={() => navigate('/clinical-projects')}>
                    <FaPlus /> {t('portal.newResearch')}
                </button>
            </aside>

            {/* ── MAIN AREA ── */}
            <div className="rp-content-area">
                {/* HEADER */}
                <header className="rp-header">
                    <div className="rp-header-search">
                        <FaSearch className="rp-search-icon" />
                        <input
                            className="rp-search-input"
                            placeholder={t('portal.searchPlaceholder')}
                        />
                    </div>
                    <div className="rp-header-right">
                        <LanguageSelector />
                        <button className="rp-header-icon"><FaBell /></button>
                        <div className="rp-user-menu-wrap">
                            <button
                                className="rp-user-pill"
                                onClick={() => setUserMenuOpen(o => !o)}
                            >
                                <div className="rp-user-info">
                                    <span className="rp-user-name">{displayUser.name}</span>
                                    <span className="rp-user-role">{displayUser.role || MOCK_RES_USER.role}</span>
                                </div>
                                <div className="rp-user-avatar">
                                    {displayUser.name.charAt(0).toUpperCase()}
                                </div>
                                <FaChevronDown className={`rp-chevron ${userMenuOpen ? 'open' : ''}`} />
                            </button>
                            {userMenuOpen && (
                                <div className="rp-dropdown">
                                    <button className="rp-dropdown-item">{t('portal.userMenu.editProfile')}</button>
                                    <button className="rp-dropdown-item">{t('portal.userMenu.myPubs')}</button>
                                    <hr className="rp-dropdown-hr" />
                                    <button className="rp-dropdown-item danger" onClick={handleLogout}>
                                        {t('portal.userMenu.logout')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* MAIN SCROLL AREA */}
                <div className="rp-scroll">
                    <div className="rp-grid">
                        {/* ─ CENTER COLUMN ─ */}
                        <div className="rp-center">
                            {/* Welcome Banner */}
                            <div className="rp-welcome-banner">
                                <h2 className="rp-welcome-title">{t('portal.welcome', { name: firstName })}</h2>
                                <p className="rp-welcome-text">{t('portal.statsSummary')}</p>
                            </div>

                            {/* Ensayos Clínicos */}
                            <div className="rp-section">
                                <div className="rp-section-header">
                                    <h3 className="rp-section-title">
                                        <FaFlask className="rp-section-icon" /> {t('portal.trialsHeader')}
                                    </h3>
                                    <button className="rp-ver-todos">{t('portal.viewAll')}</button>
                                </div>
                                <div className="rp-trials-grid">
                                    {MOCK_TRIALS.map(trial => (
                                        <div key={trial.id} className="rp-trial-card">
                                            <div className="rp-trial-header">
                                                <span
                                                    className="rp-trial-status"
                                                    style={{ color: trial.statusColor, borderColor: trial.statusColor }}
                                                >
                                                    {trial.status}
                                                </span>
                                                <span className="rp-trial-phase">{trial.phase}</span>
                                            </div>
                                            <h4 className="rp-trial-title">{trial.title}</h4>
                                            <div className="rp-trial-progress-label">
                                                <span>{trial.progressLabel}</span>
                                                <span className="rp-trial-pct" style={{ color: trial.statusColor }}>
                                                    {trial.progress}%
                                                </span>
                                            </div>
                                            <div className="rp-trial-bar-bg">
                                                <div
                                                    className="rp-trial-bar-fill"
                                                    style={{ width: `${trial.progress}%`, background: trial.statusColor }}
                                                />
                                            </div>
                                            <div className="rp-trial-milestone">
                                                <FaFileAlt className="rp-milestone-icon" />
                                                <span>{t('portal.nextMilestone', { milestone: trial.milestone })}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Ciclo de Vida con IA */}
                            <div className="rc-widget cycle-widget">
                                <h3 className="rc-widget-title">{t('lifecycle.title')}</h3>
                                <p className="rc-widget-desc">{t('lifecycle.subtitle')}</p>
                                <div className="rc-cycle-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginTop: '15px' }}>
                                    {LIFECYCLE_ACTIONS.map(action => (
                                        <button
                                            key={action.label}
                                            className="rc-cycle-item"
                                            onClick={() => action.route && navigate(action.route)}
                                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#1a1b1c', border: '1px solid #333', padding: '15px', borderRadius: '12px', color: '#fff', cursor: 'pointer' }}
                                        >
                                            <span style={{ fontSize: '1.5rem', marginBottom: '8px', color: '#FF6600' }}>{action.icon}</span>
                                            <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>{action.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Publicaciones Recientes */}
                            <div className="rp-section">
                                <div className="rp-section-header">
                                    <h3 className="rp-section-title">
                                        <FaBookOpen className="rp-section-icon" /> {t('portal.pubsHeader')}
                                    </h3>
                                    <button className="rp-ver-todos">{t('portal.viewAll')}</button>
                                </div>
                                <div className="rp-pubs-list">
                                    {MOCK_PUBLICATIONS.map(pub => (
                                        <div key={pub.id} className="rp-pub-card">
                                            <div className={`rp-pub-icon ${pub.type.toLowerCase()}`}>
                                                <FaFileAlt />
                                                <span>{pub.type}</span>
                                            </div>
                                            <div className="rp-pub-body">
                                                <p className="rp-pub-title">{pub.title}</p>
                                                <p className="rp-pub-meta">
                                                    Publicado en: <em>{pub.journal}</em> · {pub.date}
                                                </p>
                                                <div className="rp-pub-footer">
                                                    <span className="rp-pub-doi">
                                                        <FaLink /> DOI: {pub.doi}
                                                    </span>
                                                    <span className="rp-pub-views">
                                                        <FaEye /> {pub.views} vistas
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* ─ RIGHT SIDEBAR ─ */}
                        <div className="rp-right">
                            {/* Mi Red y Solicitudes */}
                            <div className="rp-widget">
                                <h3 className="rp-widget-title"><FaUsers /> {t('portal.networkHeader')}</h3>

                                {networkAccepted === null && (
                                    <div className="rp-network-request">
                                        <div className="rp-net-avatar">E</div>
                                        <div className="rp-net-info">
                                            <p className="rp-net-name">Dra. Elena Martínez</p>
                                            <p className="rp-net-role">Inmunóloga Clínica</p>
                                        </div>
                                        <div className="rp-net-actions">
                                            <button className="rp-net-accept" onClick={() => setNetworkAccepted(true)}>
                                                <FaCheck />
                                            </button>
                                            <button className="rp-net-reject" onClick={() => setNetworkAccepted(false)}>
                                                <FaTimes />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {networkAccepted === true && (
                                    <p className="rp-net-accepted">{t('portal.accepted', { name: 'Dra. Elena Martínez' })}</p>
                                )}
                                {networkAccepted === false && (
                                    <p className="rp-net-rejected">{t('portal.rejected')}</p>
                                )}

                                {/* Grant card */}
                                <div className="rp-grant-card">
                                    <div className="rp-grant-header">
                                        <FaGift className="rp-grant-icon" />
                                        <span className="rp-grant-label">NUEVA BECA</span>
                                    </div>
                                    <p className="rp-grant-title">HealthTech Innovators 2024</p>
                                    <p className="rp-grant-desc">Fondos de hasta $150k para investigación IA.</p>
                                    <button className="rp-grant-btn">{t('portal.grantBtn')}</button>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="rp-stats-row">
                                <div className="rp-stat-card">
                                    <p className="rp-stat-label">Índice-H</p>
                                    <p className="rp-stat-value">{MOCK_RES_USER.hIndex}</p>
                                </div>
                                <div className="rp-stat-card">
                                    <p className="rp-stat-label">Citas</p>
                                    <p className="rp-stat-value">{MOCK_RES_USER.cites}</p>
                                </div>
                            </div>

                            {/* Quick links */}
                            <div className="rp-widget">
                                <h3 className="rp-widget-title"><FaLink /> {t('portal.quickResources')}</h3>
                                <div className="rp-quick-links">
                                    <Link to="/scientific-events" className="rp-quick-link">📅 {t('portal.agenda')}</Link>
                                    <Link to="/researcher-library?tab=plantilla" className="rp-quick-link">📄 {t('portal.formats')}</Link>
                                    <Link to="/researcher-library?tab=video" className="rp-quick-link">▶ {t('portal.tutorials')}</Link>
                                    <Link to="/researcher-library" className="rp-quick-link">📚 {t('portal.library')}</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResearcherPortal;
